import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {ModalController} from '@ionic/angular';
import {FasePrecedenteAperta} from '../../_models/FasePrecedenteAperta';
import {OrdineKey} from '../../_models/Ordine';
import {StatoEnum} from '../../_models/generated/stato-enum';

/** Le fasi di un singolo ordine di produzione, come vengono mostrate nell'elenco. */
export interface GruppoDocumento {
  idDocumento: number;
  fasi: FasePrecedenteAperta[];
}

const chiaveOrdine = (f: FasePrecedenteAperta): OrdineKey =>
  ({idDocumento: f.idDocumento, idRiga: f.idRiga, idRigaLavorazione: f.idRigaLavorazione});

/**
 * Le chiavi preselezionate di un elenco: tutte, incomplete comprese, tranne le fasi che il server
 * salterà di sicuro (FR-013 della feature 130). Unica fonte della regola: la usa questo componente e
 * la usa la modale ordine (feature 151) per conoscere la selezione iniziale senza attendere un evento.
 */
export function chiaviPreselezionate(fasi: FasePrecedenteAperta[] | null | undefined): OrdineKey[] {
  return (fasi ?? []).filter(f => !f.saraSaltata).map(chiaveOrdine);
}

/**
 * Elenco selezionabile delle righe/fasi a monte ancora aperte (issue #3477).
 *
 * Due forme (feature 151): come **modale** del Termina operatore (`inline` falso, default, invariata) e
 * **in linea** dentro la card «Aggiorna fasi riga» della modale ordine (`inline` vero: niente header, né
 * footer CONFERMA/ANNULLA; la selezione esce da `selezioneChange` a ogni modifica dell'utente).
 *
 * ⚠️ La conferma con **zero** voci selezionate è un esito legittimo e distinto dall'annullo: è il
 * modo con cui l'operatore dice «chiudo solo la mia fase». Per questo il pulsante di conferma non è
 * **mai** disabilitato e la sua etichetta dichiara cosa sta per succedere.
 */
@Component({
  selector: 'app-interfaccia-manuale-chiudi-fasi',
  templateUrl: './interfaccia-manuale-chiudi-fasi.component.html',
  styleUrls: ['./interfaccia-manuale-chiudi-fasi.component.scss'],
  standalone: false
})
export class ChiudiFasiPrecedentiComponent implements OnInit {
  @Input() fasi: FasePrecedenteAperta[] = [];
  @Input() inline = false;
  /**
   * Emesso solo sulle modifiche dell'utente, mai in `ngOnInit`: la selezione iniziale la conosce già il
   * genitore con `chiaviPreselezionate`, ed emetterla durante il rilevamento delle modifiche del
   * genitore ne cambierebbe un binding già controllato.
   */
  @Output() selezioneChange = new EventEmitter<OrdineKey[]>();

  /** Chiavi selezionate, indicizzate come `idDocumento-idRiga-idRigaLavorazione`. */
  selezionate = new Set<string>();
  gruppi: GruppoDocumento[] = [];
  confermando = false;
  protected readonly statoEnum = StatoEnum;

  constructor(private modalCtrl: ModalController) {
  }

  /** Quante fasi verranno chiuse: dice all'operatore quanto durerà e quante scritture produrrà. */
  get numeroSelezionate(): number {
    return this.selezionate.size;
  }

  /** Selezionate che il server salterà comunque: l'etichetta non deve prometterne la chiusura. */
  get numeroSelezionateSaltate(): number {
    return this.fasi.filter(f => f.saraSaltata && this.isSelezionata(f)).length;
  }

  get etichettaConferma(): string {
    return this.numeroSelezionate === 0
      ? 'TERMINA SOLO QUESTA FASE'
      : `CHIUDI ${this.numeroSelezionate} FASI + CORRENTE`;
  }

  ngOnInit() {
    // Preselezionate tutte (FR-013), incomplete comprese: la distinzione serve a far decidere
    // l'operatore, non a decidere al posto suo. Fanno eccezione le fasi che il server salterà di
    // sicuro — invio precedente in corso o fallito: preselezionarle farebbe promettere all'etichetta
    // di conferma chiusure che non avverranno. Restano comunque selezionabili, perché lo stato è una
    // fotografia e un invio in attesa può concludersi prima della conferma.
    this.fasi = this.fasi ?? [];
    this.selezionate = new Set<string>(chiaviPreselezionate(this.fasi).map(c => this.chiaveDi(c)));
    this.gruppi = this.raggruppaPerDocumento(this.fasi);
  }

  chiaveDi = (fase: OrdineKey): string =>
    `${fase.idDocumento}-${fase.idRiga}-${fase.idRigaLavorazione}`;

  isSelezionata = (fase: FasePrecedenteAperta): boolean => this.selezionate.has(this.chiaveDi(fase));

  toggle(fase: FasePrecedenteAperta) {
    const chiave = this.chiaveDi(fase);
    if (this.selezionate.has(chiave)) {
      this.selezionate.delete(chiave);
    } else {
      this.selezionate.add(chiave);
    }
    this.selezioneChange.emit(this.chiaviSelezionate());
  }

  selezionaTutte = () => {
    this.selezionate = new Set<string>(this.fasi.map(f => this.chiaveDi(f)));
    this.selezioneChange.emit(this.chiaviSelezionate());
  };

  deselezionaTutte = () => {
    this.selezionate = new Set<string>();
    this.selezioneChange.emit(this.chiaviSelezionate());
  };

  /** Una fase è incompleta quando la quantità lavorata non arriva all'ordinata. */
  isIncompleta = (fase: FasePrecedenteAperta): boolean =>
    (fase.quantitaLavorata ?? 0) < (fase.quantita ?? 0);

  /** In lavorazione **adesso** da un altro operatore: l'operatore può scegliere di aspettare. */
  isInLavorazione = (fase: FasePrecedenteAperta): boolean => fase.stato === StatoEnum.AVANZAMENTO;

  /**
   * Conferma. Con selezione vuota si esce comunque con `confirm` e un array vuoto: è un esito
   * legittimo, non un annullo.
   */
  confirm() {
    if (this.confermando) {
      return;
    }
    this.confermando = true;
    this.modalCtrl.dismiss(this.chiaviSelezionate(), 'confirm');
  }

  /** Annulla: non viene terminata nemmeno la fase corrente. */
  cancel = () => this.modalCtrl.dismiss(null, 'cancel');

  /** Le chiavi selezionate, nell'ordine in cui l'elenco le mostra. */
  private chiaviSelezionate(): OrdineKey[] {
    return this.fasi.filter(f => this.isSelezionata(f)).map(chiaveOrdine);
  }

  private raggruppaPerDocumento(fasi: FasePrecedenteAperta[]): GruppoDocumento[] {
    const gruppi: GruppoDocumento[] = [];
    // L'elenco arriva già ordinato per (idDocumento, idRiga, numFase): l'intestazione resta anche
    // con un documento solo, così la lettura non cambia forma fra i due casi.
    fasi.forEach(fase => {
      const gruppo = gruppi.find(g => g.idDocumento === fase.idDocumento);
      if (gruppo) {
        gruppo.fasi.push(fase);
        return;
      }
      gruppi.push({idDocumento: fase.idDocumento, fasi: [fase]});
    });
    return gruppi;
  }
}
